import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import BuscadorCatalogo, { coincideBusqueda } from "./BuscadorCatalogo";
import Cargando from "./Cargando";
import { Ayuda } from "../context/AyudaContext";

const formatoFecha = (valor) =>
  valor
    ? new Date(valor).toLocaleString("es-NI", { dateStyle: "medium", timeStyle: "short" })
    : "";

// Tipo de cambio vigente (córdobas por cada dólar). Cada cambio queda en el
// historial; las cotizaciones ya guardadas conservan el que tenían.
function GestionarTipoCambio() {
  const [vigente, setVigente] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [tasa, setTasa] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [exito, setExito] = useState("");
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  const cargar = () => {
    setCargando(true);
    apiFetch("/api/tipo-cambio")
      .then((data) => {
        setVigente(data?.vigente || null);
        setHistorial(data?.historial || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setExito("");
    const valor = Number(tasa);
    if (!Number.isFinite(valor) || valor <= 0) {
      setError("Escribe un tipo de cambio válido, por ejemplo 36.6243.");
      return;
    }
    setGuardando(true);
    try {
      await apiFetch("/api/tipo-cambio", {
        method: "POST",
        body: JSON.stringify({ tasa: valor }),
      });
      setTasa("");
      setExito("Tipo de cambio actualizado.");
      cargar();
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const historialVisible = historial.filter((h) => coincideBusqueda(busqueda, [formatoFecha(h.desde), h.tasa, h.por]));

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.cardIcono}>💱</span>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--tema-titulo, #0b3c60)" }}>Tipo de cambio</h3>
          <p style={{ margin: "0.1rem 0 0", fontSize: "0.78rem", color: "#8993a1" }}>
            Córdobas por cada dólar (C$ por US$)
          </p>
        </div>
      </div>

      {cargando ? (
        <Cargando compacto />
      ) : (
        <>
          <div style={styles.vigente}>
            {vigente ? (
              <>
                <div style={styles.vigenteEtiqueta}>Tipo de cambio vigente</div>
                <div style={styles.vigenteValor}>C${vigente.tasa.toFixed(4)}</div>
                <div style={styles.vigenteDetalle}>
                  Actualizado el {formatoFecha(vigente.desde)}
                  {vigente.por ? ` por ${vigente.por}` : ""}
                </div>
              </>
            ) : (
              <div style={{ color: "#a3402f", fontSize: "0.88rem" }}>
                Todavía no hay un tipo de cambio guardado. Escríbelo abajo para que el sistema
                convierta automáticamente las cotizaciones en dólares.
              </div>
            )}
          </div>

          <form onSubmit={guardar} style={styles.form}>
            <label style={styles.etiqueta}>
              {vigente ? "Nuevo tipo de cambio" : "Tipo de cambio"}
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="Ej. 36.6243"
                style={styles.input}
              />
              <button type="submit" disabled={guardando || !tasa} style={styles.botonAgregar}>
                {guardando ? "Guardando..." : "Guardar"}
              </button>
            </div>
            <Ayuda>
            <p style={styles.nota}>
              Se usa en las cotizaciones en dólares nuevas. Las ya guardadas conservan el tipo de
              cambio con el que se registraron.
            </p>
            </Ayuda>
          </form>

          {error && <p style={{ color: "#a3402f" }}>{error}</p>}
          {exito && <p style={{ color: "#2f7a54" }}>{exito}</p>}

          {historial.length > 0 && (
            <>
              <div style={{ ...styles.etiqueta, marginBottom: "0.4rem" }}>Historial de cambios</div>
              <BuscadorCatalogo
                valor={busqueda}
                onCambiar={setBusqueda}
                placeholder="Buscar por fecha, tipo de cambio o persona…"
                mostrados={historialVisible.length}
                total={historial.length}
              />
              <div className="table-scroll" style={styles.tablaWrap}>
                <table style={styles.tabla}>
                  <thead>
                    <tr style={{ textAlign: "left", borderBottom: "1px solid #d3d9df" }}>
                      <th style={styles.th}>Fecha</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Tipo de cambio</th>
                      <th style={styles.th}>Registrado por</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historialVisible.map((h, i) => (
                      <tr
                        key={h.id}
                        style={{
                          borderBottom: "1px solid #eef1f3",
                          background: i === 0 ? "#e6f3ec" : i % 2 === 1 ? "#fafbfc" : "transparent",
                        }}
                      >
                        <td style={styles.td}>
                          {formatoFecha(h.desde)}
                          {i === 0 && <span style={styles.badgeVigente}>Vigente</span>}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                          C${h.tasa.toFixed(4)}
                        </td>
                        <td style={styles.td}>{h.por || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.1rem 1.2rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    maxWidth: 640,
    margin: "0 auto",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.4rem",
    marginBottom: "1rem",
  },
  cardIcono: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "#dcf0e6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    flexShrink: 0,
  },
  vigente: {
    textAlign: "center",
    border: "1px solid #cfe6d8",
    background: "#f4f9f6",
    borderRadius: 10,
    padding: "0.9rem 1rem",
    marginBottom: "1rem",
  },
  vigenteEtiqueta: { fontSize: "0.76rem", fontWeight: 700, color: "#2f7a54", textTransform: "uppercase", letterSpacing: "0.04em" },
  vigenteValor: { fontSize: "1.9rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums" },
  vigenteDetalle: { fontSize: "0.78rem", color: "#5b6472" },
  form: {
    border: "1px solid #eef1f3",
    borderRadius: 10,
    padding: "0.9rem 1rem",
    marginBottom: "1rem",
    background: "#fafbfc",
  },
  etiqueta: { display: "block", fontSize: "0.78rem", fontWeight: 600, color: "#5b6472", marginBottom: "0.35rem" },
  input: {
    flex: "1 1 160px",
    boxSizing: "border-box",
    padding: "0.45rem 0.6rem",
    border: "1px solid #d3d9df",
    borderRadius: 6,
    fontFamily: "inherit",
    fontSize: "0.9rem",
  },
  nota: { margin: "0.6rem 0 0", fontSize: "0.76rem", color: "#8993a1" },
  botonAgregar: {
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "0.5rem 1.1rem",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
  },
  tablaWrap: { border: "1px solid #eef1f3", borderRadius: 10 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" },
  th: {
    padding: "0.55rem 0.6rem",
    background: "#eef1f3",
    fontSize: "0.76rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
  },
  td: { padding: "0.5rem 0.6rem", color: "var(--tema-titulo, #0b3c60)" },
  badgeVigente: {
    marginLeft: "0.5rem",
    padding: "0.1rem 0.5rem",
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#2f7a54",
    background: "#dcf0e6",
  },
};

export default GestionarTipoCambio;
